import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { 
    ExcelData, 
    CitySums, 
    CityTerminal, 
    CompareFiles, 
    SnackbarState,
    ProcessStats 
} from './types';
import { 
    readExcelFile, 
    calculateSums, 
    matchAndUpdateData, 
    exportToExcel 
} from './utils';

export interface FileValidation {
    isValid: boolean;
    errors: string[];
    rowCount?: number;
    fileSize?: number;
}

export const useEnhancedCompare = () => {
    // Основное состояние
    const [salesReport, setSalesReport] = useState<string>('');
    const [archiveOrders, setArchiveOrders] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [processingProgress, setProcessingProgress] = useState(0);
    const [processedOrders, setProcessedOrders] = useState<number>(0);
    const [matchedOrders, setMatchedOrders] = useState<number>(0);
    const [selectedDate, setSelectedDate] = useState('');
    const [processedData, setProcessedData] = useState<ExcelData | null>(null);
    const [isProcessed, setIsProcessed] = useState(false);
    const [sums, setSums] = useState<CitySums>({ total: 0 });
    const [citySums, setCitySums] = useState<CitySums>({ total: 0 });
    const [cityTerminals, setCityTerminals] = useState<CityTerminal[]>([]);
    
    // Новые состояния для улучшенного UX
    const [files, setFiles] = useState<CompareFiles>({
        salesReport: null,
        archiveOrders: null
    });
    const [fileValidations, setFileValidations] = useState<{
        salesReport: FileValidation;
        archiveOrders: FileValidation;
    }>({
        salesReport: { isValid: false, errors: [] },
        archiveOrders: { isValid: false, errors: [] }
    });
    
    // Уведомления и диалоги
    const [snackbar, setSnackbar] = useState<SnackbarState>({
        open: false,
        message: '',
        severity: 'success'
    });
    const [openCityManager, setOpenCityManager] = useState(false);
    
    // Управление отменой операций
    const abortControllerRef = useRef<AbortController | null>(null);

    // Инициализация
    useEffect(() => {
        document.title = 'Сопоставление Excel файлов | Zerde';
        fetchCityTerminals();
        
        // Восстановление данных из localStorage при необходимости
        const savedDate = localStorage.getItem('compare_selected_date');
        if (savedDate) {
            setSelectedDate(savedDate);
        }
        
        return () => {
            // Очистка при размонтировании
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    // Загрузка данных о городах и терминалах
    const fetchCityTerminals = async () => {
        try {
            const response = await axios.get('https://tbl.zerdegroup.kz/api/city-terminals');
            if (response.data.success) {
                setCityTerminals(response.data.terminals);
            } else {
                showNotification('Ошибка при загрузке данных о городах', 'error');
            }
        } catch (error) {
            console.error('Error fetching city terminals:', error);
            showNotification('Ошибка при загрузке данных о городах', 'error');
        }
    };

    // Валидация файлов
    const validateFile = useCallback(async (file: File, fileType: 'salesReport' | 'archiveOrders'): Promise<FileValidation> => {
        try {
            // Проверка размера файла (не более 50MB)
            if (file.size > 50 * 1024 * 1024) {
                return {
                    isValid: false,
                    errors: ['Файл слишком большой (максимум 50MB)'],
                    fileSize: file.size
                };
            }

            // Проверка типа файла
            const validTypes = [
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'application/vnd.ms-excel'
            ];
            
            if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls)$/i)) {
                return {
                    isValid: false,
                    errors: ['Неподдерживаемый формат файла. Используйте .xlsx или .xls'],
                    fileSize: file.size
                };
            }

            // Быстрая проверка содержимого
            const data = await readExcelFile(file, fileType);
            const firstSheet = Object.values(data)[0];
            
            if (!firstSheet || firstSheet.data.length === 0) {
                return {
                    isValid: false,
                    errors: ['Файл пуст или не содержит данных'],
                    fileSize: file.size
                };
            }

            return {
                isValid: true,
                errors: [],
                rowCount: firstSheet.data.length,
                fileSize: file.size
            };
        } catch (error) {
            return {
                isValid: false,
                errors: ['Ошибка при чтении файла'],
                fileSize: file.size
            };
        }
    }, []);

    // Уведомления
    const showNotification = useCallback((message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
        setSnackbar({ open: true, message, severity });
    }, []);

    const handleCloseSnackbar = useCallback(() => {
        setSnackbar(prev => ({ ...prev, open: false }));
    }, []);

    // Загрузка данных аптеки по дате
    const fetchPharmacyDeliveryData = useCallback(async (date: string) => {
        setIsLoading(true);
        setProcessingProgress(10);

        try {
            const response = await axios.get(`https://tbl.zerdegroup.kz/api/pharmacy-delivery-date/${date}`);
            setProcessingProgress(50);
            
            if (response.data.success) {
                const newCitySums: CitySums = {
                    total: response.data.data.totalSum || 0
                };

                if (response.data.data.citySums) {
                    // Ключи теперь - это created_by (аптеки), а не города
                    Object.entries(response.data.data.citySums).forEach(([pharmacyKey, sum]) => {
                        newCitySums[pharmacyKey] = Number(sum) || 0;
                    });
                }

                setCitySums(newCitySums);
                setProcessingProgress(100);
                showNotification('Данные загружены успешно', 'success');
            } else {
                showNotification(response.data.message, 'error');
            }
        } catch (error) {
            console.error('Error fetching pharmacy delivery data:', error);
            showNotification('Произошла ошибка при получении данных', 'error');
        } finally {
            setTimeout(() => {
                setIsLoading(false);
                setProcessingProgress(0);
            }, 500);
        }
    }, [showNotification]);

    // Обработка изменения файла
    const handleFileChange = useCallback(async (
        event: React.ChangeEvent<HTMLInputElement>,
        setFile: React.Dispatch<React.SetStateAction<string>>,
        fileType: 'salesReport' | 'archiveOrders'
    ) => {
        const file = event.target.files?.[0];
        if (file) {
            setFile(file.name);
            setFiles(prev => ({ ...prev, [fileType]: file }));

            // Валидация файла
            const validation = await validateFile(file, fileType);
            setFileValidations(prev => ({
                ...prev,
                [fileType]: validation
            }));

            if (!validation.isValid) {
                showNotification(`Ошибка в файле: ${validation.errors.join(', ')}`, 'error');
                return;
            }

            // Для Sales Report сразу рассчитываем суммы
            if (fileType === 'salesReport') {
                try {
                    const salesReportData = await readExcelFile(file, 'salesReport');
                    setSums(calculateSums(salesReportData, null, cityTerminals));
                    showNotification('Sales Report загружен успешно', 'success');
                } catch (error) {
                    console.error('Error calculating initial sums:', error);
                    showNotification('Ошибка при обработке Sales Report', 'error');
                }
            } else {
                showNotification('Archive Orders загружен успешно', 'success');
            }
        }

        event.target.value = '';
    }, [cityTerminals, validateFile, showNotification]);

    // Очистка файла
    const clearFile = useCallback((
        inputId: string,
        setFile: React.Dispatch<React.SetStateAction<string>>,
        fileType: 'salesReport' | 'archiveOrders'
    ) => {
        setFile('');
        setFiles(prev => ({ ...prev, [fileType]: null }));
        setFileValidations(prev => ({
            ...prev,
            [fileType]: { isValid: false, errors: [] }
        }));
        
        if (fileType === 'salesReport') {
            setSums({ total: 0 });
        }
        
        const input = document.getElementById(inputId) as HTMLInputElement;
        if (input) {
            input.value = '';
        }
        
        showNotification(`${fileType === 'salesReport' ? 'Sales Report' : 'Archive Orders'} удален`, 'info');
    }, [showNotification]);

    // Основная обработка
    const handleStart = useCallback(async () => {
        if (!files.salesReport || !files.archiveOrders) return;

        // Создаем AbortController для возможности отмены
        abortControllerRef.current = new AbortController();
        
        setIsLoading(true);
        setProcessedOrders(0);
        setMatchedOrders(0);
        setIsProcessed(false);
        setProcessingProgress(0);

        try {
            // Шаг 1: Чтение файлов
            setProcessingProgress(20);
            const [salesReportData, archiveOrdersData] = await Promise.all([
                readExcelFile(files.salesReport, 'salesReport'),
                readExcelFile(files.archiveOrders, 'archiveOrders')
            ]);

            // Проверка на отмену
            if (abortControllerRef.current?.signal.aborted) {
                throw new Error('Operation cancelled');
            }

            // Шаг 2: Сопоставление данных
            setProcessingProgress(60);
            const { data: updatedSalesReport, stats } = matchAndUpdateData(
                salesReportData, 
                archiveOrdersData, 
                cityTerminals
            );

            // Шаг 3: Финальные расчеты
            setProcessingProgress(80);
            setProcessedData(updatedSalesReport);
            setProcessedOrders(stats.processed);
            setMatchedOrders(stats.matched);
            setSums(calculateSums(updatedSalesReport, archiveOrdersData, cityTerminals));
            
            setProcessingProgress(100);
            setIsProcessed(true);

            showNotification(
                `Обработка завершена! Обработано заказов: ${stats.processed}, всего совпадений: ${stats.matched}\n` +
                `Через склад КД: ${stats.warehouseMatched}, через адрес: ${stats.addressMatched}, не найдено: ${stats.notMatched}`,
                'success'
            );
        } catch (error: any) {
            console.error('Error processing files:', error);
            if (error.message === 'Operation cancelled') {
                showNotification('Обработка отменена', 'info');
            } else {
                showNotification('Произошла ошибка при обработке файлов', 'error');
            }
        } finally {
            setTimeout(() => {
                setIsLoading(false);
                setProcessingProgress(0);
            }, 1000);
            abortControllerRef.current = null;
        }
    }, [files, cityTerminals, showNotification]);

    // Отмена обработки
    const handleCancel = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
    }, []);

    // Сброс всех данных
    const handleReset = useCallback(() => {
        clearFile('salesReport', setSalesReport, 'salesReport');
        clearFile('archiveOrders', setArchiveOrders, 'archiveOrders');
        setIsProcessed(false);
        setProcessedData(null);
        setProcessedOrders(0);
        setMatchedOrders(0);
        setSums({ total: 0 });
        showNotification('Данные сброшены', 'info');
    }, [clearFile, showNotification]);

    // Скачивание результата
    const handleDownload = useCallback(() => {
        if (processedData && files.salesReport) {
            exportToExcel(processedData, files.salesReport);
            showNotification('Файл скачан успешно', 'success');
        }
    }, [processedData, files.salesReport, showNotification]);

    // Очистка всех файлов
    const handleClearAll = useCallback(() => {
        clearFile('salesReport', setSalesReport, 'salesReport');
        clearFile('archiveOrders', setArchiveOrders, 'archiveOrders');
        setIsProcessed(false);
        setProcessedData(null);
    }, [clearFile]);

    // Обработка изменения даты
    const handleDateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const date = e.target.value;
        setSelectedDate(date);
        localStorage.setItem('compare_selected_date', date);
        
        if (date) {
            fetchPharmacyDeliveryData(date);
        }
    }, [fetchPharmacyDeliveryData]);

    // Статистика обработки
    const processingStats: ProcessStats = {
        processed: processedOrders,
        matched: matchedOrders
    };

    return {
        // Состояние
        salesReport,
        archiveOrders,
        isLoading,
        processingProgress,
        processedOrders,
        matchedOrders,
        selectedDate,
        processedData,
        isProcessed,
        sums,
        citySums,
        files,
        fileValidations,
        snackbar,
        openCityManager,
        cityTerminals,
        processingStats,
        
        // Сеттеры состояния
        setSalesReport,
        setArchiveOrders,
        setOpenCityManager,
        
        // Методы
        showNotification,
        handleCloseSnackbar,
        handleFileChange,
        clearFile,
        handleStart,
        handleCancel,
        handleReset,
        handleDownload,
        handleClearAll,
        handleDateChange,
        fetchPharmacyDeliveryData
    };
};
